import { useState } from 'react';
import { Lightbulb, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const SUGGESTIONS = [
  'Ny elbil på vej',
  'Prisændring',
  'Variantopdatering',
  'Nyhed/artikel',
  'Forhandlerinfo',
  'Andet',
];

export function TipUsSection() {
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 5) {
      toast.error('Skriv lidt mere', { description: 'Dit tip skal være mindst 5 tegn.' });
      return;
    }
    if (trimmed.length > 2000) {
      toast.error('For langt', { description: 'Maks. 2000 tegn.' });
      return;
    }

    setSubmitting(true);
    const composedMessage = topic ? `[${topic}] ${trimmed}` : trimmed;
    const payload = {
      type: 'tip',
      message: composedMessage,
      email: email.trim() || null,
      page_url: typeof window !== 'undefined' ? window.location.href : null,
    };
    const { submitForm } = await import('@/lib/submission');
    const res = await submitForm({
      formName: 'tip_us',
      table: 'feedback',
      payload,
      email: payload.email,
      meta: { topic: topic ?? null },
      perform: () => supabase.from('feedback').insert(payload),
    });
    if (res.ok) {
      toast.success('Tak for dit tip!', { description: 'Vi kigger på det hurtigst muligt.' });
      setMessage('');
      setEmail('');
      setTopic(null);
    } else {
      toast.error('Noget gik galt', { description: res.userMessage ?? 'Prøv igen.' });
    }
    setSubmitting(false);
  };

  return (
    <section className="container mx-auto px-4 py-12">
      <div className="relative overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/10 via-background to-primary/10 p-6 sm:p-8">
        <div className="pointer-events-none absolute -right-16 -top-16 w-56 h-56 rounded-full bg-accent/15 blur-3xl" />
        <div className="relative grid gap-6 lg:grid-cols-[1fr,1.2fr] items-start">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/15 text-accent text-xs font-semibold">
              <Lightbulb className="w-3.5 h-3.5" />
              Tip os
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold leading-tight">
              Har du et tip om elbiler?
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              Nyhed, prisændring, ny variant, lanceringsdato eller noget helt andet —
              alle tips er velkomne. Vi læser dem alle.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 bg-card rounded-xl p-4 sm:p-5 shadow-sm border">
            <div>
              <Label className="text-xs text-muted-foreground mb-2 block">Hvad handler det om? (valgfrit)</Label>
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setTopic(topic === s ? null : s)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      topic === s
                        ? 'bg-accent text-accent-foreground border-accent'
                        : 'bg-background hover:bg-secondary border-input'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="tip-message" className="sr-only">Dit tip</Label>
              <Textarea
                id="tip-message"
                placeholder="Skriv dit tip her... gerne med kilde-link hvis relevant."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                rows={4}
                required
              />
            </div>

            <div>
              <Label htmlFor="tip-email" className="sr-only">E-mail</Label>
              <Input
                id="tip-email"
                type="email"
                placeholder="Din e-mail (valgfri – hvis vi har opfølgende spørgsmål)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={255}
              />
            </div>

            <Button type="submit" disabled={submitting} className="w-full sm:w-auto" variant="accent">
              <Send className="w-4 h-4" />
              {submitting ? 'Sender...' : 'Send tip'}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
